import axios from "axios";
import { CheckToken } from "../../login/AccessToken";

/**
 * Discharge Template APIs — backing the Discharge Template Management screen.
 *
 *   upload_discharge_template/  POST multipart/form-data — upload org template
 *   list_discharge_templates/   POST JSON — list org templates, optionally bind
 *   generate_discharge_summary/ POST JSON — generate summary for an admission
 *   validate_discharge_payload/ POST JSON — validate the discharge payload
 *
 * All live on the main Django backend, so they use REACT_APP_BACKEND_ROUTE_URL.
 * Responses follow { status, data | message } envelope. Every response is
 * HTTP 200 — read `status`, not the HTTP code.
 */

const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;

function authHeaders() {
  CheckToken();
  return {
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  };
}

const TEMPLATE_NAME_KEY = "hlthtek_discharge_template_original_names";

export function rememberTemplateOriginalName(id, fileName) {
  if (id == null || !fileName) return;
  try {
    const map = JSON.parse(localStorage.getItem(TEMPLATE_NAME_KEY) || "{}");
    map[String(id)] = fileName;
    localStorage.setItem(TEMPLATE_NAME_KEY, JSON.stringify(map));
  } catch (err) {
    console.error("rememberTemplateOriginalName:", err);
  }
}

export function getRememberedTemplateOriginalName(id) {
  try {
    const map = JSON.parse(localStorage.getItem(TEMPLATE_NAME_KEY) || "{}");
    return map[String(id)] || null;
  } catch (err) {
    return null;
  }
}

// POST upload_discharge_template/ — multipart/form-data file upload.
export async function uploadDischargeTemplate(file) {
  const namedFile = new File([file], file.name, { type: file.type || undefined });
  const formData = new FormData();
  formData.append("file", namedFile);
  formData.append("file_name", file.name);
  formData.append("name", file.name);

  const res = await axios.post(`${BASE}upload_discharge_template/`, formData, {
    headers: authHeaders(),
  });
  return res?.data;
}

// POST list_discharge_templates/ — list org templates, optionally bind to admission.
// If admission_id and template_id are omitted, just lists.
export async function listDischargeTemplates(payload = {}) {
  const res = await axios.post(
    `${BASE}list_discharge_templates/`,
    payload,
    { headers: authHeaders() }
  );
  return res?.data;
}

// POST generate_discharge_summary/ — generate summary for an admission.
// Body forwarded verbatim; typically includes admission_id.
export async function generateDischargeSummary(payload) {
  const res = await axios.post(
    `${BASE}generate_discharge_summary/`,
    payload,
    { headers: authHeaders() }
  );
  return res?.data;
}

// POST validate_discharge_payload/ — validate discharge payload for an admission.
// Body forwarded verbatim; typically includes admission_id and summary data.
export async function validateDischargePayload(payload) {
  const res = await axios.post(
    `${BASE}validate_discharge_payload/`,
    payload,
    { headers: authHeaders() }
  );
  return res?.data;
}

// POST delete_discharge_template/ — delete a discharge template by ID.
export async function deleteDischargeTemplate(templateId) {
  const res = await axios.post(
    `${BASE}delete_discharge_template/`,
    { template_id: templateId },
    { headers: authHeaders() }
  );
  return res?.data;
}
